import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MagnifyingGlass,
    BookOpen,
    ChalkboardTeacher,
    Student,
    Question,
    CaretDown,
    ArrowRight
} from "@phosphor-icons/react";

const PageContainer = styled.div`
  background: #0d1117;
  color: white;
  min-height: 100vh;
  padding-top: 5rem;
`;

const HeroSection = styled.section`
  padding: 6rem 2rem 4rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 100%;
    background: radial-gradient(circle at top center, rgba(58, 183, 255, 0.15) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #fff, #b3b3b3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #8b949e;
  max-width: 600px;
  margin: 0 auto 3rem;
  line-height: 1.6;
`;

const SearchContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  position: relative;

  input {
    width: 100%;
    padding: 1.25rem 1.5rem 1.25rem 3.5rem;
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: white;
    font-size: 1.1rem;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: #3AB7FF;
      background: rgba(255, 255, 255, 0.08);
      box-shadow: 0 0 0 4px rgba(58, 183, 255, 0.1);
    }

    &::placeholder {
      color: #8b949e;
    }
  }

  svg {
    position: absolute;
    left: 1.25rem;
    top: 50%;
    transform: translateY(-50%);
    color: #8b949e;
    font-size: 1.5rem;
  }
`;

const ContentLayout = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 6rem;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Sidebar = styled.aside`
  position: sticky;
  top: 100px;
  height: max-content;

  @media (max-width: 968px) {
    display: none;
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NavItem = styled.li<{ $active?: boolean }>`
  a {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    color: ${props => props.$active ? '#3AB7FF' : '#8b949e'};
    background: ${props => props.$active ? 'rgba(58, 183, 255, 0.1)' : 'transparent'};
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      color: ${props => props.$active ? '#3AB7FF' : 'white'};
      background: rgba(255, 255, 255, 0.05);
    }
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
`;

const Section = styled.section`
  scroll-margin-top: 100px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: white;
    margin: 0;
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(58, 183, 255, 0.1);
    color: #3AB7FF;
    font-size: 1.5rem;
  }
`;

const ArticleList = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const ArticleCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(58, 183, 255, 0.3);
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }
`;

const ArticleImage = styled.div`
  width: 100%;
  height: 200px;
  background: #161b22;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.8;
  }

  .placeholder {
    color: #8b949e;
    font-size: 3rem;
    opacity: 0.2;
  }
`;

const ArticleContent = styled.div`
  padding: 1.5rem;

  h3 {
    font-size: 1.25rem;
    color: white;
    margin-bottom: 0.75rem;
  }

  p {
    color: #8b949e;
    line-height: 1.6;
    margin-bottom: 1rem;
  }

  ul, ol {
    margin: 0;
    padding-left: 1.5rem;
    color: #8b949e;
    li {
      margin-bottom: 0.5rem;
      line-height: 1.5;
    }
  }

  .highlight {
    color: #3AB7FF;
    font-weight: 500;
  }
`;

const FAQAccordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FAQItem = styled.div<{ $isOpen: boolean }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;

  button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem;
    background: transparent;
    border: none;
    color: white;
    font-size: 1.1rem;
    font-weight: 600;
    text-align: left;
    cursor: pointer;

    svg {
      transition: transform 0.3s ease;
      transform: rotate(${props => props.$isOpen ? '180deg' : '0'});
      color: #8b949e;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.05);
    }
  }
`;

const FAQContent = styled(motion.div)`
  color: #8b949e;
  line-height: 1.6;
  padding: 0 1.25rem;

  p {
    margin: 0 0 1.25rem 0;
  }
`;

const SupportBanner = styled.div`
  background: linear-gradient(135deg, rgba(58, 183, 255, 0.1), rgba(13, 17, 23, 1));
  border: 1px solid rgba(58, 183, 255, 0.2);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  margin-top: 4rem;

  h3 {
    font-size: 1.5rem;
    color: white;
    margin-bottom: 1rem;
  }

  p {
    color: #8b949e;
    margin-bottom: 1.5rem;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #3AB8FF;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.2s;

    &:hover {
      background: #50c1ff;
      transform: translateY(-2px);
    }
  }
`;

const HelpCenterPage: React.FC = () => {
    const { t } = useTranslation();
    const [activeSection, setActiveSection] = useState('getting-started');
    const [openFAQ, setOpenFAQ] = useState<number | null>(null);

    const scrollToSection = (id: string) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const toggleFAQ = (index: number) => {
        setOpenFAQ(openFAQ === index ? null : index);
    };

    return (
        <PageContainer>
            <HeroSection>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Title>{t('Spark AI Help Center')}</Title>
                    <Subtitle>
                        {t("Welcome to the Spark AI Education Platform Help Center! This guide will provide you with step-by-step instructions on how to navigate the platform and make the most out of our AI-powered features.")}
                    </Subtitle>
                </motion.div>
            </HeroSection>

            <ContentLayout>
                <Sidebar>
                    <h3 style={{ color: 'white', marginBottom: '1.5rem', fontSize: '1.25rem' }}>{t('Table of Contents')}</h3>
                    <NavList>
                        <NavItem $active={activeSection === 'getting-started'}>
                            <a href="#getting-started" onClick={(e) => { e.preventDefault(); scrollToSection('getting-started'); }}>
                                <BookOpen weight="fill" />
                                {t('Getting Started')}
                            </a>
                        </NavItem>
                        <NavItem $active={activeSection === 'for-teachers'}>
                            <a href="#for-teachers" onClick={(e) => { e.preventDefault(); scrollToSection('for-teachers'); }}>
                                <ChalkboardTeacher weight="fill" />
                                {t('For Teachers: Navigation')}
                            </a>
                        </NavItem>
                        <NavItem $active={activeSection === 'for-students'}>
                            <a href="#for-students" onClick={(e) => { e.preventDefault(); scrollToSection('for-students'); }}>
                                <Student weight="fill" />
                                {t('For Students: Joining')}
                            </a>
                        </NavItem>
                        <NavItem $active={activeSection === 'faq'}>
                            <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}>
                                <Question weight="fill" />
                                {t('Frequently Asked Questions')}
                            </a>
                        </NavItem>
                    </NavList>
                </Sidebar>

                <MainContent>
                    {/* Getting Started Section */}
                    <Section id="getting-started">
                        <SectionHeader>
                            <div className="icon">
                                <BookOpen weight="fill" />
                            </div>
                            <h2>{t('Getting Started')}</h2>
                        </SectionHeader>
                        <ArticleList>
                            <ArticleCard>
                                <ArticleContent>
                                    <h3>{t('Creating an Account')}</h3>
                                    <ol>
                                        <li>{t('Navigate to the Spark platform and select Sign up.')}</li>
                                        <li>{t('Register your account using your preferred email or sign in using SSO.')}</li>
                                        <li>{t('Validate your email using the verification link sent to your inbox.')}</li>
                                        <li>{t('Customize your profile (Teacher or Student) to tailor the experience to your needs.')}</li>
                                    </ol>
                                </ArticleContent>
                            </ArticleCard>
                            <ArticleCard>
                                <ArticleContent>
                                    <h3>{t('Logging In')}</h3>
                                    <ol>
                                        <li>{t('Enter your registered email and password on the Login Page.')}</li>
                                        <li>{t('If you forget your password, simply click on Forgot password? to receive a reset link safely to your designated email.')}</li>
                                    </ol>
                                </ArticleContent>
                            </ArticleCard>
                        </ArticleList>
                    </Section>

                    {/* For Teachers Section */}
                    <Section id="for-teachers">
                        <SectionHeader>
                            <div className="icon">
                                <ChalkboardTeacher weight="fill" />
                            </div>
                            <h2>{t('For Teachers: Navigation & Features')}</h2>
                        </SectionHeader>
                        <p style={{ color: '#8b949e', marginBottom: '2rem', fontSize: '1.1rem' }}>
                            {t('As an educator, Spark operates as your AI classroom assistant. Here is exactly how to navigate your tools so you can focus entirely on teaching!')}
                        </p>
                        <ArticleList>
                            <ArticleCard>
                                <ArticleImage>
                                    <ChalkboardTeacher className="placeholder" weight="duotone" />
                                </ArticleImage>
                                <ArticleContent>
                                    <h3>{t('Teacher Dashboard & Desk')}</h3>
                                    <p><span className="highlight">{t('How to navigate:')}</span> {t('Upon logging in, you will be directed to your main Dashboard. From the sidebar or bottom navigation, click on Desk to access your daily workspace.')}</p>
                                    <p><span className="highlight">{t('What it does:')}</span> {t('The Dashboard gives you a quick overview of recent activity and allows you to seamlessly launch into exams, grading, or games.')}</p>
                                </ArticleContent>
                            </ArticleCard>

                            <ArticleCard>
                                <ArticleImage>
                                    <BookOpen className="placeholder" weight="duotone" />
                                </ArticleImage>
                                <ArticleContent>
                                    <h3>{t('Creating Exams (Smart & Digital)')}</h3>
                                    <p><span className="highlight">{t('How to navigate:')}</span> {t('On your Dashboard, locate the Create Exam or Smart Exam Creator card. Click Start to begin configuring your new test.')}</p>
                                    <p><span className="highlight">{t('Smart Exam Creator:')}</span> {t('Uses advanced AI to generate standardized and detailed exams. Choose a subject, specify the difficulty and topics, and our AI will build an exam tailor-made for your curriculum.')}</p>
                                    <p><span className="highlight">{t('Digital Exam Creator:')}</span> {t('Do you already have a paper test? Use the Digital Exam Creator to upload scans (PDFs, Images), and Spark will automatically extract questions to build a digital version.')}</p>
                                </ArticleContent>
                            </ArticleCard>

                            <ArticleCard>
                                <ArticleContent>
                                    <h3>{t('AI Edugame Generator')}</h3>
                                    <p><span className="highlight">{t('How to navigate:')}</span> {t('Click on Edugames from your Dashboard or sidebar.')}</p>
                                    <p><span className="highlight">{t('What it does:')}</span> {t('Gamify your classroom! Transform dry topics into interactive educational games. Input your lesson topics, and the AI will configure an interactive environment complete with challenges, a scoreboard, and engaging multiplayer interactions for students to enjoy.')}</p>
                                </ArticleContent>
                            </ArticleCard>

                            <ArticleCard>
                                <ArticleContent>
                                    <h3>{t('Automated Grading')}</h3>
                                    <p><span className="highlight">{t('How to navigate:')}</span> {t('Click on Grade in your main navigation menu.')}</p>
                                    <p><span className="highlight">{t('What it does:')}</span> {t('Provides instant grading and tailored feedback! Once students submit their digital exams, Spark’s AI evaluates the submissions. It highlights areas for improvement and can even process handwritten submissions.')}</p>
                                </ArticleContent>
                            </ArticleCard>

                            <ArticleCard>
                                <ArticleContent>
                                    <h3>{t('Insights & Analytics')}</h3>
                                    <p><span className="highlight">{t('How to navigate:')}</span> {t('Select Insights from your sidebar.')}</p>
                                    <p><span className="highlight">{t('What it does:')}</span> {t('A comprehensive breakdown of your students\' performance. Monitor overall class metrics, view granular reporting on a specific student, and find knowledge gaps using Spark\'s data-driven tracking mechanisms.')}</p>
                                </ArticleContent>
                            </ArticleCard>

                            <ArticleCard>
                                <ArticleContent>
                                    <h3>{t('Library')}</h3>
                                    <p><span className="highlight">{t('How to navigate:')}</span> {t('Click on Library in your left-hand menu.')}</p>
                                    <p><span className="highlight">{t('What it does:')}</span> {t('Your personalized vault containing all your past exams, lessons, and interactive games. Sort, search, and reuse past materials. Easily bring up an effective exam from last year and redeploy it for a new classroom in a matter of seconds.')}</p>
                                </ArticleContent>
                            </ArticleCard>
                        </ArticleList>
                    </Section>

                    {/* For Students Section */}
                    <Section id="for-students">
                        <SectionHeader>
                            <div className="icon">
                                <Student weight="fill" />
                            </div>
                            <h2>{t('For Students: Joining an Exam or Game')}</h2>
                        </SectionHeader>
                        <p style={{ color: '#8b949e', marginBottom: '2rem', fontSize: '1.1rem' }}>
                            {t('For students, jumping right into a learning session is simple and fast. Note: You don\'t need a registered account just to join a session if you are given a code!')}
                        </p>
                        <ArticleList>
                            <ArticleCard>
                                <ArticleImage>
                                    <Student className="placeholder" weight="duotone" />
                                </ArticleImage>
                                <ArticleContent>
                                    <ol>
                                        <li>
                                            <span className="highlight">{t('Obtain the Code:')}</span> {t('Get the Access Code from your teacher.')}
                                            <ul style={{ marginTop: '0.5rem' }}>
                                                <li>{t('Exam codes look like SPK-XXXXX.')}</li>
                                                <li>{t('Game codes are typically 6-character strings.')}</li>
                                            </ul>
                                        </li>
                                        <li style={{ marginTop: '1rem' }}>
                                            <span className="highlight">{t('Navigate to Join:')}</span> {t('From the platform\'s main page or dashboard, click on the Join Exam or Join Game button.')}
                                        </li>
                                        <li style={{ marginTop: '1rem' }}>
                                            <span className="highlight">{t('Enter Code:')}</span> {t('Input the required code and follow the instructions on the screen to enter the lobby!')}
                                        </li>
                                    </ol>
                                </ArticleContent>
                            </ArticleCard>
                        </ArticleList>
                    </Section>

                    {/* FAQ Section */}
                    <Section id="faq">
                        <SectionHeader>
                            <div className="icon">
                                <Question weight="fill" />
                            </div>
                            <h2>{t('Frequently Asked Questions')}</h2>
                        </SectionHeader>
                        <FAQAccordion>
                            {[
                                {
                                    q: t('Can I access Spark on my mobile device?'),
                                    a: t('Yes! Spark features a responsive design and many functionalities are optimized for mobile access.')
                                },
                                {
                                    q: t('Are my tests and student data secure?'),
                                    a: t('Absolutely. Spark adheres to secure standards ensuring that tests, metrics, and student data are kept strictly confidential and accessible solely by verified classroom supervisors.')
                                },
                                {
                                    q: t('The AI created a question I don\'t agree with, can I modify it?'),
                                    a: t('Yes. All AI-generated exams and lesson plans are fully customizable. The AI provides a strong foundation, but you always have the final say on the actual curriculum!')
                                }
                            ].map((faq, index) => (
                                <FAQItem key={index} $isOpen={openFAQ === index}>
                                    <button onClick={() => toggleFAQ(index)}>
                                        {faq.q}
                                        <CaretDown weight="bold" />
                                    </button>
                                    <AnimatePresence>
                                        {openFAQ === index && (
                                            <FAQContent
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <p>{faq.a}</p>
                                            </FAQContent>
                                        )}
                                    </AnimatePresence>
                                </FAQItem>
                            ))}
                        </FAQAccordion>
                    </Section>

                    <SupportBanner>
                        <h3>{t('Need further assistance?')}</h3>
                        <p>{t('Please reach out to your school\'s IT administrator or the Spark Support Team.')}</p>
                        <a href="mailto:support@sparkskool.com">
                            {t('Contact Support')} <ArrowRight weight="bold" />
                        </a>
                    </SupportBanner>

                </MainContent>
            </ContentLayout>
        </PageContainer>
    );
};

export default HelpCenterPage;
